import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useState } from 'react';
import { toast } from 'sonner';
import type { IRevertSummary } from '../../../extViewmodels/sdk/models';
import { getDisplayName } from '../../../models/NameUtils';
import { copyToClipboard } from '../../../models/UiUtils';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { IImportJob } from '../../../viewmodels/AppViewModels';
import { Button } from '../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ClipboardIcon } from '../../../web/components/svgs/icons/ClipboardIcon';
import { brandPrimary, brandPrimaryHover } from '../../../web/styles/colors';
import { hasRevertWarnings } from '../RevertResultModal/utils';
import { ConditionalAccess } from '../ConditionalAccess';
import { styleSheet } from './styles';

interface IProps {
	columnCount: number;
	downloadFile?(e: React.MouseEvent<HTMLElement>): void;
	importJob: IImportJob;
	isReverting?: boolean;
	onForceRevertImport?: ((e: React.MouseEvent<HTMLElement>) => void) | null;
	onViewRevertSummary?(): void;
	revertImport?(e: React.MouseEvent<HTMLElement>): void;
	showButton?: boolean;
}

/** Contacts section stats — always shown when count > 0 */
interface IDetailStat {
	count: number;
	label: string;
	isWarning?: boolean;
}

function getContactStats(summary: IRevertSummary): IDetailStat[] {
	return [
		{ count: summary.contactsDeleted, label: 'Archived' },
		{ count: summary.contactsRestored, label: 'Restored' },
		{ count: summary.contactsMergedReverted, label: 'Merges reversed' },
		{ count: summary.contactsSkippedModifiedSinceImport, label: 'Skipped', isWarning: true },
		{ count: summary.contactsMergeRevertFailed, label: 'Merge failures', isWarning: true },
	];
}

function getRelatedStats(summary: IRevertSummary): IDetailStat[] {
	return [
		{ count: summary.policiesArchived, label: 'Policies archived' },
		{ count: summary.householdsDeleted, label: 'Households deleted' },
		{ count: summary.notesDeleted, label: 'Notes deleted' },
		{ count: summary.automationsCancelled, label: 'Automations cancelled' },
		{ count: summary.companiesDeleted, label: 'Companies deleted' },
	];
}

function _ImportJobAccount({
	columnCount,
	downloadFile,
	importJob,
	isReverting,
	onForceRevertImport,
	onViewRevertSummary,
	revertImport,
	showButton,
}: IProps) {
	const [expanded, setExpanded] = useState(false);
	const isReverted = !!importJob.revertSummary;
	const hasWarnings = isReverted && hasRevertWarnings(importJob.revertSummary!);

	const copyImportId = () => {
		const done = copyToClipboard(importJob.id);
		if (done) {
			toast.success('Copied import ID to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard...');
		}
	};

	const toggleExpanded = () => {
		if (isReverted) {
			setExpanded(prev => !prev);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
		if (e.target !== e.currentTarget) {
			return;
		}
		if (isReverted && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			toggleExpanded();
		}
	};

	if (isReverting) {
		return (
			<tr>
				<td className={css(styleSheet.td, styleSheet.center)} colSpan={columnCount}>
					<LoadingSpinner type='small' />
				</td>
			</tr>
		);
	}

	return (
		<>
			<tr
				className={isReverted ? css(styleSheet.revertedRow) : undefined}
				onClick={isReverted ? toggleExpanded : undefined}
				onKeyDown={isReverted ? handleKeyDown : undefined}
				tabIndex={isReverted ? 0 : undefined}
				aria-expanded={isReverted ? expanded : undefined}
				role={isReverted ? 'row' : undefined}
			>
				<td className={css(styleSheet.td)}>{moment(importJob.dateCreatedUtc).format('MM-DD-YYYY') || ''}</td>
				<td className={css(styleSheet.td, styleSheet.tdTruncate)}>{getDisplayName(importJob.userReference, true)}</td>
				<td className={css(styleSheet.td, styleSheet.tdTruncate)}>
					<ConditionalAccess
						permission={AdminPermission.OnlyForDevelopers}
						or={AdminPermission.ModifyAccount}
						elseChildren={<>{importJob.additionalFields?.importFileName || ''}</>}
					>
						<Button
							variant='link'
							size='sm'
							styles={[styleSheet.fileNameLink]}
							onClick={e => {
								e.stopPropagation();
								downloadFile?.(e);
							}}
						>
							{importJob.additionalFields?.importFileName || ''}
						</Button>
					</ConditionalAccess>
				</td>
				<td className={css(styleSheet.td)}>{importJob.recordsProcessed || ''}</td>
				<td className={css(styleSheet.td)}>
					<span className={css(styleSheet.jobIdCell)}>
						{importJob.id || ''}
						<ConditionalAccess permission={AdminPermission.OnlyForDevelopers} elseChildren={null}>
							<span
								className={css(styleSheet.copy)}
								onClick={e => {
									e.stopPropagation();
									copyImportId();
								}}
							>
								<ClipboardIcon fillColor={brandPrimaryHover} hoverColor={brandPrimary} />
							</span>
						</ConditionalAccess>
					</span>
				</td>
				<td className={css(styleSheet.td)}>
					{isReverted ? (
						<span className={css(styleSheet.statusCell)}>
							<span className={css(styleSheet.chevron, expanded && styleSheet.chevronExpanded)} aria-hidden='true'>
								&#x25B8;
							</span>
							<span className={css(styleSheet.badge, hasWarnings ? styleSheet.badgeWarning : styleSheet.badgeClean)}>
								{hasWarnings ? 'Warnings' : 'Clean'}
							</span>
						</span>
					) : (
						<>
							{showButton && (
								<ConditionalAccess permission={AdminPermission.RevertImport}>
									{!onForceRevertImport && (
										<Button id={importJob.id} variant='primary' size='sm' onClick={revertImport}>
											Revert
										</Button>
									)}
								</ConditionalAccess>
							)}
							<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
								{!!onForceRevertImport && (
									<Button id={importJob.id} variant='destructive' size='sm' onClick={onForceRevertImport}>
										Force
									</Button>
								)}
							</ConditionalAccess>
						</>
					)}
				</td>
			</tr>
			{isReverted && expanded && (
				<tr>
					<td className={css(styleSheet.detailTd)} colSpan={columnCount}>
						<RevertDetail summary={importJob.revertSummary!} onViewFullDetails={onViewRevertSummary} />
					</td>
				</tr>
			)}
		</>
	);
}

/** Inline expandable detail showing revert summary statistics. */
function RevertDetail({ summary, onViewFullDetails }: { summary: IRevertSummary; onViewFullDetails?(): void }) {
	const contactStats = getContactStats(summary).filter(s => s.count > 0);
	const relatedStats = getRelatedStats(summary).filter(s => s.count > 0);

	return (
		<div>
			<div className={css(styleSheet.detailGrid)}>
				{contactStats.length > 0 && (
					<div className={css(styleSheet.detailSection)}>
						<div className={css(styleSheet.detailSectionTitle)}>Contacts</div>
						<ul className={css(styleSheet.detailList)}>
							{contactStats.map(stat => (
								<li key={stat.label} className={css(styleSheet.detailItem)}>
									<span className={css(stat.isWarning ? styleSheet.detailCountWarning : styleSheet.detailCount)}>
										{stat.count}
									</span>{' '}
									{stat.label}
								</li>
							))}
						</ul>
					</div>
				)}
				{relatedStats.length > 0 && (
					<div className={css(styleSheet.detailSection)}>
						<div className={css(styleSheet.detailSectionTitle)}>Related</div>
						<ul className={css(styleSheet.detailList)}>
							{relatedStats.map(stat => (
								<li key={stat.label} className={css(styleSheet.detailItem)}>
									<span className={css(styleSheet.detailCount)}>{stat.count}</span> {stat.label}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
			{onViewFullDetails && (
				<div className={css(styleSheet.detailFooter)}>
					<Button
						variant='link'
						size='sm'
						styles={[styleSheet.detailLink]}
						onClick={e => {
							e.stopPropagation();
							onViewFullDetails();
						}}
					>
						View full details &rarr;
					</Button>
				</div>
			)}
		</div>
	);
}

export const ImportJobAccount = observer(_ImportJobAccount);
